import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Check } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';

const DJANGO_BASE_URL = 'https://agritrack.samperlabs.com';

export const Pricing = () => {
  const { t } = useI18n();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const plans = [
    {
      name: t('pricing.starter'),
      price: t('pricing.starterPrice'),
      period: t('pricing.starterPeriod'),
      recommended: true,
      features: [
        t('pricing.starterFeature1'),
        t('pricing.starterFeature2'),
        t('pricing.starterFeature3'),
      ],
      ctaVariant: 'primary',
    },
    {
      name: t('pricing.pro'),
      price: t('pricing.proPrice'),
      period: t('pricing.proPeriod'),
      recommended: false,
      features: [
        t('pricing.proFeature1'),
        t('pricing.proFeature2'),
        t('pricing.proFeature3'),
        t('pricing.proFeature4'),
      ],
      ctaVariant: 'secondary',
    },
    {
      name: t('pricing.enterprise'),
      price: t('pricing.enterprisePrice'),
      period: '',
      recommended: false,
      description: t('pricing.enterpriseDesc'),
      features: [
        t('pricing.enterpriseFeature1'),
        t('pricing.enterpriseFeature2'),
        t('pricing.enterpriseFeature3'),
      ],
      ctaVariant: 'secondary',
    },
  ];

  return (
    <section
      ref={ref}
      id="pricing"
      className="py-24 lg:py-32 bg-surface"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 lg:mb-16"
        >
          <h2
            className="text-3xl sm:text-4xl font-black text-primary tracking-tight"
            data-testid="pricing-title"
          >
            {t('pricing.title')}
          </h2>
          <p className="text-muted-foreground mt-4">
            {t('pricing.subtitle')}
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 + index * 0.1, duration: 0.5 }}
              className="relative"
              data-testid={`pricing-card-${index}`}
            >
              {plan.recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <span className="bg-primary text-white px-4 py-1 rounded-full text-xs font-mono uppercase tracking-[0.15em]">
                    {t('pricing.recommended')}
                  </span>
                </div>
              )}

              <Card
                className={`h-full flex flex-col bg-white border-2 ${
                  plan.recommended
                    ? 'border-primary shadow-elevated'
                    : 'border-primary/10 shadow-soft'
                } rounded-2xl hover:shadow-lg transition-shadow duration-300`}
              >
                <CardHeader className="p-8 pb-4">
                  <h3 className="text-lg font-bold text-primary">
                    {plan.name}
                  </h3>
                  <div className="mt-4 flex items-baseline">
                    <span
                      className={`font-black text-primary ${
                        plan.price === t('pricing.enterprisePrice')
                          ? 'text-2xl'
                          : 'text-4xl'
                      }`}
                    >
                      {plan.price}
                    </span>
                    {plan.period && (
                      <span className="text-muted-foreground ml-2">
                        {plan.period}
                      </span>
                    )}
                  </div>
                  {plan.description && (
                    <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
                      {plan.description}
                    </p>
                  )}
                </CardHeader>

                <CardContent className="flex-1 p-8 pt-4">
                  <ul className="space-y-4">
                    {plan.features.map((feature, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-3 text-sm"
                      >
                        <div
                          className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                            plan.recommended
                              ? 'bg-forest-100'
                              : 'bg-primary/10'
                          }`}
                        >
                          <Check
                            className={`w-3 h-3 ${
                              plan.recommended
                                ? 'text-forest-700'
                                : 'text-primary'
                            }`}
                          />
                        </div>
                        <span className="text-muted-foreground">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter className="p-8 pt-0">
                  <a
                    href={`${DJANGO_BASE_URL}/register/`}
                    className="w-full"
                    data-testid={`pricing-cta-${index}`}
                  >
                    <Button
                      className={`w-full py-4 rounded-xl font-semibold transition-all duration-200 ${
                        plan.ctaVariant === 'primary'
                          ? 'bg-primary hover:bg-primary-light text-white hover:-translate-y-0.5 hover:shadow-lg'
                          : 'bg-gray-100 hover:bg-gray-200 text-primary'
                      }`}
                    >
                      {t('pricing.startFree')}
                    </Button>
                  </a>
                </CardFooter>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
